import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, GraduationCap } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { MONTHS } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const now = new Date();
const YEARS = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);
const ORDER = [null, "Hadir", "Izin", "Alpa"];
const CELL_STYLE = {
  Hadir: "bg-emerald-500 text-white hover:bg-emerald-600",
  Izin: "bg-amber-400 text-white hover:bg-amber-500",
  Alpa: "bg-red-500 text-white hover:bg-red-600",
};

function CellButton({ status, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`h-8 w-8 rounded-lg text-xs font-bold transition-colors duration-200 ${
        status ? CELL_STYLE[status] : "border border-dashed border-slate-300 text-slate-300 hover:border-primary hover:text-primary"
      }`}
    >
      {status ? status[0] : "+"}
    </button>
  );
}

function TeacherAttendanceCard({ month, year }) {
  const [data, setData] = useState(null);

  const load = () =>
    api.get("/attendance/teachers", { params: { month, year } }).then((res) => setData(res.data)).catch(() => {});

  useEffect(() => {
    setData(null);
    load();
  }, [month, year]);

  const toggle = async (row, no) => {
    const current = row.marks[String(no)] || null;
    const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length];
    try {
      await api.post("/attendance/teachers", { class_id: row.class_id, month, year, meeting_no: no, status: next });
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan absensi guru"));
    }
  };

  const rows = data?.rows || [];
  const maxN = Math.max(0, ...rows.map((r) => r.meetings_per_month));

  return (
    <Card className="border-border shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <GraduationCap className="h-5 w-5 text-primary" />
          Absensi Guru per Kelas {data ? `${data.month_name} ${data.year}` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-white dark:bg-[#0B2A5B]">Kelas</TableHead>
              <TableHead>Guru</TableHead>
              {Array.from({ length: maxN }, (_, i) => (
                <TableHead key={i} className="text-center">P{i + 1}</TableHead>
              ))}
              <TableHead className="text-center">Kehadiran</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!data ? (
              <TableRow>
                <TableCell colSpan={maxN + 3} className="py-10 text-center text-muted-foreground">Memuat data...</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={maxN + 3} className="py-10 text-center text-muted-foreground" data-testid="attendance-teacher-empty">
                  Belum ada kelas.
                </TableCell>
              </TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.class_id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`attendance-teacher-row-${r.class_id}`}>
                <TableCell className="sticky left-0 bg-white font-semibold dark:bg-[#0B2A5B]">
                  {r.class_name}
                  <span className="block text-xs font-normal text-muted-foreground">{r.instrument}</span>
                </TableCell>
                <TableCell className="text-muted-foreground">{r.teacher_name}</TableCell>
                {Array.from({ length: maxN }, (_, i) => {
                  const no = i + 1;
                  if (no > r.meetings_per_month) {
                    return <TableCell key={no} className="text-center text-muted-foreground/40">—</TableCell>;
                  }
                  return (
                    <TableCell key={no} className="text-center">
                      <CellButton
                        status={r.marks[String(no)] || null}
                        onClick={() => toggle(r, no)}
                        testid={`attendance-teacher-cell-${r.class_id}-${no}`}
                      />
                    </TableCell>
                  );
                })}
                <TableCell className="text-center" data-testid={`attendance-teacher-summary-${r.class_id}`}>
                  <span className={`font-bold ${r.hadir === r.meetings_per_month && r.meetings_per_month > 0 ? "text-emerald-600" : "text-foreground"}`}>
                    {r.hadir}/{r.meetings_per_month}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function Attendance() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  const load = () =>
    api.get("/attendance", { params: { month, year } }).then((res) => setData(res.data)).catch(() => {});

  useEffect(() => {
    setData(null);
    load();
  }, [month, year]);

  const toggle = async (row, no) => {
    const current = row.marks[String(no)] || null;
    const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length];
    try {
      await api.post("/attendance", { student_id: row.student_id, month, year, meeting_no: no, status: next });
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan absensi"));
    }
  };

  const rows = data?.rows || [];
  const maxN = Math.max(0, ...rows.map((r) => r.meetings_per_month));
  const totalHadir = rows.reduce((s, r) => s + r.hadir, 0);
  const totalIzin = rows.reduce((s, r) => s + r.izin, 0);
  const totalAlpa = rows.reduce((s, r) => s + r.alpa, 0);

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Absensi</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Kehadiran siswa dan guru per pertemuan — jumlah pertemuan mengikuti pengaturan tiap kelas.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36" data-testid="attendance-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="attendance-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="attendance-legend">
        <span className="text-xs text-muted-foreground">Klik sel untuk menandai:</span>
        <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">H = Hadir ({totalHadir})</Badge>
        <Badge className="bg-amber-400 text-white hover:bg-amber-400">I = Izin ({totalIzin})</Badge>
        <Badge className="bg-red-500 text-white hover:bg-red-500">A = Alpa ({totalAlpa})</Badge>
        <Badge variant="secondary">kosong = belum ditandai</Badge>
      </div>

      <Tabs defaultValue="siswa">
        <TabsList>
          <TabsTrigger value="siswa" data-testid="tab-absensi-siswa">Siswa</TabsTrigger>
          <TabsTrigger value="guru" data-testid="tab-absensi-guru">Guru</TabsTrigger>
        </TabsList>

        <TabsContent value="siswa" className="mt-4">
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                Absensi Siswa {data ? `${data.month_name} ${data.year}` : ""}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white dark:bg-[#0B2A5B]">Siswa</TableHead>
                    <TableHead>Kelas</TableHead>
                    {Array.from({ length: maxN }, (_, i) => (
                      <TableHead key={i} className="text-center">P{i + 1}</TableHead>
                    ))}
                    <TableHead className="text-center">Kehadiran</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!data ? (
                    <TableRow>
                      <TableCell colSpan={maxN + 3} className="py-10 text-center text-muted-foreground">Memuat data...</TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={maxN + 3} className="py-10 text-center text-muted-foreground" data-testid="attendance-empty">
                        Belum ada siswa aktif.
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.student_id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`attendance-row-${r.student_id}`}>
                      <TableCell className="sticky left-0 bg-white font-semibold dark:bg-[#0B2A5B]">{r.name}</TableCell>
                      <TableCell className="text-muted-foreground">{r.class_name}</TableCell>
                      {Array.from({ length: maxN }, (_, i) => {
                        const no = i + 1;
                        if (no > r.meetings_per_month) {
                          return <TableCell key={no} className="text-center text-muted-foreground/40">—</TableCell>;
                        }
                        return (
                          <TableCell key={no} className="text-center">
                            <CellButton
                              status={r.marks[String(no)] || null}
                              onClick={() => toggle(r, no)}
                              testid={`attendance-cell-${r.student_id}-${no}`}
                            />
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center" data-testid={`attendance-summary-${r.student_id}`}>
                        <span className={`font-bold ${r.hadir === r.meetings_per_month && r.meetings_per_month > 0 ? "text-emerald-600" : "text-foreground"}`}>
                          {r.hadir}/{r.meetings_per_month}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="guru" className="mt-4">
          <TeacherAttendanceCard month={month} year={year} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
